import { Enum, ProjectType, ProjectTypes } from '../../common/constants'
import { FocusedView } from '../../components/Items/ItemsRoot'
import { VisitSummaryItem, WorkareaSummary } from '../../components/Items/itemSummaryModel'
import { VisitFilter } from '../../components/Items/TimeAnalysis/timeAnalysisModel'
import { LegacyWorkareaData, Visit } from '../../domain/jobs/jobs'
import { WorkareaGroupWithWorkareas } from '../../domain/workareas/group'

export const JobDetailTabKeys = {
  Details: 'details',
  Plan: 'plan',
  Proposals: 'proposals',
  Schedule: 'schedule',
  Visits: 'visits',
  Analysis: 'analysis',
  Invoicing: 'invoicing'
} as const
export type JobDetailTabKey = Enum<typeof JobDetailTabKeys>

export const buildTabKeys = (canAccessInvoicing: boolean): JobDetailTabKey[] => {
  const keys: JobDetailTabKey[] = [
    JobDetailTabKeys.Details,
    JobDetailTabKeys.Plan,
    JobDetailTabKeys.Proposals,
    JobDetailTabKeys.Schedule,
    JobDetailTabKeys.Visits,
    JobDetailTabKeys.Analysis
  ]

  if (canAccessInvoicing) {
    keys.push(JobDetailTabKeys.Invoicing)
  }

  return keys
}

export const focusedViewForTab = (tab: JobDetailTabKey): FocusedView | undefined => {
  switch (tab) {
    case JobDetailTabKeys.Plan:
      return 'workarea-estimated'
    case JobDetailTabKeys.Visits:
      return 'visits'
    case JobDetailTabKeys.Analysis:
      return 'costing-and-analysis'
    case JobDetailTabKeys.Invoicing:
      return 'invoicing'
    default:
      return undefined
  }
}

/** Job routes carry a slug like "1234-front-walkway"; only the leading id matters. */
export const parseJobId = (routeParam: number | string | undefined): number => {
  if (typeof routeParam === 'number') {
    return routeParam
  }
  const id = parseInt(String(routeParam ?? '').split('-')[0], 10)
  return isNaN(id) ? 0 : id
}

export const jobRouteBase = (jobType: ProjectType): string => {
  return {
    [ProjectTypes.Project]: 'projects',
    [ProjectTypes.Ticket]: 'tickets',
    [ProjectTypes.Maintenance]: 'maintenance'
  }[jobType]
}

/** Service tickets never had the visit-notes modal. */
export const showVisitNotesLink = (jobType: ProjectType): boolean => {
  return jobType !== ProjectTypes.Ticket
}

export type JobAnalysisTotals = {
  hours: number
  price: number
  grossProfit: number
  netProfit: number
}

export type JobAnalysis = {
  estimated: JobAnalysisTotals
  actual: JobAnalysisTotals
  final: JobAnalysisTotals
}

const sumSummaries = (summaries: WorkareaSummary[], getValue: (summary: WorkareaSummary) => number) => {
  return summaries.filter(x => !x.isOnHold).reduce((total, x) => total + getValue(x), 0)
}

export const buildJobAnalysis = (workareaSummaries: WorkareaSummary[]): JobAnalysis => {
  return {
    estimated: {
      hours: sumSummaries(workareaSummaries, x => x.estimatedHours),
      price: sumSummaries(workareaSummaries, x => x.estimatedPrice),
      grossProfit: sumSummaries(workareaSummaries, x => x.estimatedGrossProfit),
      netProfit: sumSummaries(workareaSummaries, x => x.estimatedNetProfit)
    },
    actual: {
      hours: sumSummaries(workareaSummaries, x => x.actualHours),
      price: sumSummaries(workareaSummaries, x => x.standardPrice),
      grossProfit: sumSummaries(workareaSummaries, x => x.actualGrossProfit),
      netProfit: sumSummaries(workareaSummaries, x => x.actualNetProfit)
    },
    final: {
      hours: sumSummaries(workareaSummaries, x => x.actualHours),
      price: sumSummaries(workareaSummaries, x => x.finalPrice),
      grossProfit: sumSummaries(workareaSummaries, x => x.actualGrossProfit),
      netProfit: sumSummaries(workareaSummaries, x => x.actualNetProfit)
    }
  }
}

export type AnalysisVisitRow = {
  compoundId: string
  workareaId: number
  date: string
  time: string
  price: number
  billed: boolean
}

export const buildAnalysisVisitRows = (
  visits: Visit[],
  workareas: LegacyWorkareaData[],
  visitSummary: VisitSummaryItem[]
): AnalysisVisitRow[] => {
  const rows: AnalysisVisitRow[] = []

  visits.forEach(visit => {
    const price = visitSummary.find(x => x.visitId === visit.id)?.totalPrice ?? 0

    visit.taskIds.forEach(workareaId => {
      if (!workareas.some(x => x.id === workareaId)) {
        return
      }

      const time = (visit.timesheets ?? [])
        .filter(x => x.taskId === workareaId)
        .reduce((total, x) => total + (x.time ?? 0), 0)

      rows.push({
        compoundId: `${visit.id}__${workareaId}`,
        workareaId,
        date: visit.date,
        time: time.toFixed(2),
        price,
        billed: visit.billed
      })
    })
  })

  return rows
}

/**
 * Legacy patched the loaded job in place so the cost-code column reflected a
 * fresh QuickBooks link without reloading the job.
 */
export const applyCostCodeQboLink = (
  workareas: LegacyWorkareaData[],
  costCodeId: number,
  qboId: string
): LegacyWorkareaData[] => {
  return workareas.map(workarea => workarea.costCodeId === costCodeId
    ? { ...workarea, costCodeQboId: qboId }
    : workarea)
}

export type AnalysisFilters = {
  enabledWorkareaIds: number[]
  visitFilters: VisitFilter[]
}

export const buildDefaultAnalysisFilters = (
  workareas: LegacyWorkareaData[],
  visitRows: AnalysisVisitRow[]
): AnalysisFilters => {
  return {
    enabledWorkareaIds: workareas.filter(x => !x.isOnHold).map(x => x.id),
    visitFilters: visitRows.map(x => ({ compoundId: x.compoundId, include: true }))
  }
}

export const applyWorkareaGroupChanges = (
  workareas: LegacyWorkareaData[],
  workareaGroups: WorkareaGroupWithWorkareas[]
): LegacyWorkareaData[] => {
  return workareaGroups.flatMap(group => group.workareas.map(update => {
    const existing = workareas.find(x => x.id === update.id)
      ?? { id: update.id, costCodeId: 0, divisionId: 0, scheduleDate: '', hours: 0, alreadyInvoiced: 0 }

    return {
      ...existing,
      workareaGroupId: update.workareaGroupId,
      name: update.name,
      master: update.master,
      masterWorkareaId: update.masterWorkareaId,
      sortIndex: update.sortIndex,
      quotedPrice: update.quotedPrice,
      recovery: update.recovery,
      pricing: update.pricing,
      workSubtotal: update.workSubtotal,
      customQuotedPrice: update.customQuotedPrice ?? undefined,
      status: update.status,
      divisionId: update.divisionId ?? 0,
      costCodeId: update.costCodeId ?? 0,
      notes: update.notes,
      customLaborRateItemId: update.customLaborRateItemId,
      showTaxInFinalPrice: update.showTaxInFinalPrice,
      completedDate: update.completedDate,
      recurringScheduleConfig: update.recurringScheduleConfig,
      customerAndInvoiceNotes: update.customerAndInvoiceNotes,
      invoiceDescription: update.invoiceDescription,
      estimatedOccurrences: update.estimatedOccurrences,
      pricingType: group.pricingType,
      isApproved: group.isApproved,
      isOnHold: group.isOnHold
    } as LegacyWorkareaData
  }))
}
